import { HocuspocusProvider } from "@hocuspocus/provider";
import { useEffect, useState } from "react";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import { useAuthToken } from "@/hooks/useAuthToken";

type CollaborationConnectionStatus =
  | "connecting"
  | "connected"
  | "disconnected";

function getServerConfirmedDocumentKey(documentId: string) {
  return `production-hub-document:${documentId}:server-confirmed`;
}

function readServerConfirmedDocument(documentId: string | null) {
  if (!documentId || typeof window === "undefined") return false;
  return (
    window.localStorage.getItem(getServerConfirmedDocumentKey(documentId)) ===
    "1"
  );
}

export function useCollaboration(documentId: string | null) {
  const { token } = useAuthToken();
  const [provider, setProvider] = useState<HocuspocusProvider | null>(null);
  const [ydoc, setYdoc] = useState<Y.Doc | null>(null);
  const [isRemoteSynced, setIsRemoteSynced] = useState(false);
  const [isLocalSynced, setIsLocalSynced] = useState(false);
  const [hasServerConfirmedDocument, setHasServerConfirmedDocument] = useState(
    () => readServerConfirmedDocument(documentId),
  );
  const [connectionStatus, setConnectionStatus] =
    useState<CollaborationConnectionStatus>("disconnected");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!documentId || !token) {
      setProvider(null);
      setYdoc(null);
      setIsRemoteSynced(false);
      setIsLocalSynced(false);
      setHasServerConfirmedDocument(false);
      setConnectionStatus("disconnected");
      setIsLoading(false);
      return;
    }

    const nextYdoc = new Y.Doc();
    setYdoc(nextYdoc);
    setIsLoading(true);
    setIsRemoteSynced(false);
    setIsLocalSynced(false);
    setHasServerConfirmedDocument(readServerConfirmedDocument(documentId));
    setConnectionStatus("connecting");

    const persistence = new IndexeddbPersistence(
      `production-hub-document:${documentId}`,
      nextYdoc,
    );
    void persistence.whenSynced
      .then(() => {
        setIsLocalSynced(true);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLocalSynced(true);
        setIsLoading(false);
      });

    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const nextProvider = new HocuspocusProvider({
      url: `${wsProtocol}//${window.location.host}/collaboration`,
      name: documentId,
      document: nextYdoc,
      token,
      onSynced: ({ state }) => {
        setIsRemoteSynced(state);
        if (state) {
          window.localStorage.setItem(
            getServerConfirmedDocumentKey(documentId),
            "1",
          );
          setHasServerConfirmedDocument(true);
        }
        setIsLoading(false);
      },
      onStatus: ({ status }) => {
        setConnectionStatus(status);
      },
      onDisconnect: () => {
        setIsRemoteSynced(false);
        setConnectionStatus("disconnected");
      },
      onClose: () => {
        setIsRemoteSynced(false);
        setConnectionStatus("disconnected");
        setIsLoading(false);
      },
    });
    setProvider(nextProvider);

    return () => {
      nextProvider.destroy();
      void persistence.destroy();
      nextYdoc.destroy();
      setProvider(null);
      setYdoc(null);
      setIsRemoteSynced(false);
      setIsLocalSynced(false);
      setHasServerConfirmedDocument(false);
      setConnectionStatus("disconnected");
    };
  }, [documentId, token]);

  return {
    ydoc,
    provider,
    isSynced: isRemoteSynced,
    isRemoteSynced,
    isLocalSynced,
    hasServerConfirmedDocument,
    isLoading,
    connectionStatus,
    canEdit: isLocalSynced && (isRemoteSynced || hasServerConfirmedDocument),
  };
}
